import { useMemo } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import { Typography } from '@mui/material'
import { ProjectModel, ProjectModelVersion } from '@/types/project-models'
import {
  getAccuracyColor,
  getModelVersionAccuracy,
  getSemanticVersion,
} from '@/utils/model-page'
import { prettifyDate } from '@/utils/time'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import ModelStatusLabel from './ModelStatusLabel'

type VersionTableProps = {
  model: ProjectModel
}

export default function VersionTable({ model }: VersionTableProps) {
  const versionColumns: MRT_ColumnDef<ProjectModelVersion>[] = [
    {
      header: 'Version',
      accessorFn: (version) => getSemanticVersion(version),
      id: 'version',
      size: 150,
    },
    {
      header: 'Accuracy',
      accessorFn: (version) => getModelVersionAccuracy(version) ?? '',
      id: 'accuracy',
      size: 150,
      Cell: ({ cell }) => {
        const accuracy = cell.getValue<string>()
        const color = getAccuracyColor(accuracy)
        return <Typography color={color}>{accuracy}</Typography>
      },
    },
    {
      header: 'Docs Used',
      accessorKey: 'document_count',
      id: 'document_count',
      size: 150,
    },
    {
      header: 'Training Completed',
      accessorFn: (version) =>
        version.training_completed_at
          ? prettifyDate(version.training_completed_at)
          : '',
      id: 'training_completed_at',
      size: 200,
    },
    {
      header: 'Status',
      accessorKey: 'training_status',
      id: 'version_status',
      size: 150,
      Cell: ({ renderedCellValue }) => {
        return (
          <ModelStatusLabel
            status={renderedCellValue as ProjectModelVersion['training_status']}
          />
        )
      },
    },
  ]

  const data = useMemo(() => {
    return [...model.project_model_versions].sort(
      (a, b) => b.version - a.version,
    )
  }, [model.project_model_versions])

  const versionTableInstance = useZerapixTable<ProjectModelVersion>({
    columns: versionColumns,
    data,
    enableTopToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: (theme) => `1px solid ${theme.palette.divider}`,
      },
    },
  })

  return <ZerapixTable<ProjectModelVersion> table={versionTableInstance} />
}
